<script setup lang="ts">
import { nextTick, ref, watch } from "vue";
import { debounce } from "vue-debounce";
import { useApiClient } from "@/api/client";
import type { NominatimResult } from "@/api/resources/campaign-planner";
import SelectedLocationDO = Modules.CampaignPlanner.Data.SelectedLocationDO;

const props = defineProps<{ modelValue?: SelectedLocationDO[] | null }>();
const emit = defineEmits<{
	"update:modelValue": [value: SelectedLocationDO[]];
}>();

const search = ref("");
const loading = ref(false);
const items = ref<NominatimResult[]>([]);
const noResults = ref(false);
const autocompleteValue = ref<NominatimResult | null>(null);
const selectedItems = ref<SelectedLocationDO[]>(props.modelValue ? [...props.modelValue] : []);

const headers = [
	{ title: "Location", key: "name", align: "start" as const },
	{
		title: "Geometry",
		key: "buffer",
		align: "end" as const,
		width: 300,
	},
	{
		title: "",
		key: "actions",
		align: "end" as const,
		sortable: false,
		width: 48,
	},
];

const bufferOptions: { label: string; value: number }[] = [
	{ label: "Location boundaries", value: 0 },
	{ label: "50m r", value: 50 },
	{ label: "100m r", value: 100 },
	{ label: "250m r", value: 250 },
	{ label: "500m r", value: 500 },
	{ label: "1 km r", value: 1_000 },
	{ label: "2 km r", value: 2_000 },
	{ label: "5 km r", value: 5_000 },
	{ label: "10 km r", value: 10_000 },
	{ label: "20 km r", value: 20_000 },
	{ label: "50 km r", value: 50_000 },
	{ label: "75 km r", value: 75_000 },
	{ label: "100 km r", value: 100_000 },
];

const getBufferOptions = (item: SelectedLocationDO) =>
	item.osm_type === "relation" ? bufferOptions : bufferOptions.filter((o) => o.value > 0);

watch(selectedItems, () => emit("update:modelValue", selectedItems.value), {
	deep: true,
});

watch(autocompleteValue, (val) => {
	if (!val) return;
	if (!selectedItems.value.some((s) => s.osm_id === val.osm_id && s.osm_type === val.osm_type)) {
		selectedItems.value.push({
			uuid: crypto.randomUUID(),
			osm_id: val.osm_id,
			name: val.display_name,
			osm_type: val.osm_type,
			lat: parseFloat(val.lat),
			lon: parseFloat(val.lon),
			buffer: val.osm_type === "relation" ? 0 : 500,
		});
	}
	nextTick(() => {
		autocompleteValue.value = null;
		search.value = "";
		items.value = [];
	});
});

const removeItem = (uuid: string) => {
	selectedItems.value = selectedItems.value.filter((s) => s.uuid !== uuid);
};

const performSearch = debounce(async (val: string) => {
	if (search.value !== val) return;
	loading.value = true;
	noResults.value = false;
	const r = await useApiClient("campaignPlanner").searchLocations(val);
	if (r.isOk()) {
		items.value = r.value;
		noResults.value = r.value.length === 0;
	}
	loading.value = false;
}, 350);

watch(search, (val) => {
	if (!val || val.length < 2) {
		items.value = [];
		noResults.value = false;
		return;
	}
	performSearch(val);
});
</script>

<template>
	<v-autocomplete
		v-model="autocompleteValue"
		v-model:search="search"
		:loading="loading"
		:items="items"
		item-title="display_name"
		return-object
		no-filter
		variant="filled"
		density="compact"
		hide-details
		clearable
		placeholder="Search location…"
		prepend-icon="fa: fa-light fa-location-dot"
	>
		<template #no-data>
			<div class="text-caption text-medium-emphasis px-4 py-2">
				<template v-if="loading">Searching…</template>
				<template v-else-if="noResults">No results found</template>
				<template v-else-if="search && search.length < 2"
					>Type at least 2 characters</template
				>
				<template v-else>Start typing to search</template>
			</div>
		</template>
	</v-autocomplete>

	<v-data-table
		v-if="selectedItems.length > 0"
		:headers="headers"
		:items="selectedItems"
		:items-per-page="-1"
		density="compact"
		hide-default-footer
		class="elevation-0 border rounded mt-2"
	>
		<template #item.name="{ item }">
			<span class="text-body-2">{{ item.name }}</span>
		</template>

		<template #item.buffer="{ item }">
			<div class="d-flex justify-end py-1">
				<v-select
					v-model="item.buffer"
					:items="getBufferOptions(item)"
					item-title="label"
					item-value="value"
					variant="filled"
					density="compact"
					hide-details
					style="max-width: 230px"
				/>
			</div>
		</template>

		<template #item.actions="{ item }">
			<v-btn
				icon="fa: fa-regular fa-trash-can"
				size="x-small"
				variant="text"
				color="error"
				@click="removeItem(item.uuid)"
			/>
		</template>

		<template #no-data>
			<div class="py-3 text-center text-caption text-medium-emphasis">
				No locations selected
			</div>
		</template>
	</v-data-table>
</template>
